import type { CompletedSaleTransaction } from '~/api/graphql/generated/types'
import type { PaymentData } from '~/constants/addPaymentDialogV2Constants'
import type { InvoiceOrEstimate } from '~/types'
import { trackEvent, TrackingEvent } from '~/utils/trackingProvider'

import { accountCreditsSelected } from './clientAccountCreditUtils'

const TRANSACTION_TYPE_CHARGE = 'Charge'

function getInvoiceType(invoice: InvoiceOrEstimate): string | undefined {
  return invoice.invoiceType ?? invoice.type
}

export interface TrackSendToTerminalClickedParams {
  businessId: string
  currentPaymentData: PaymentData
  invoice: InvoiceOrEstimate
}

export function trackSendToTerminalClicked({
  invoice,
  currentPaymentData,
  businessId,
}: TrackSendToTerminalClickedParams): void {
  const accountCreditsApplied = accountCreditsSelected(
    currentPaymentData.accountCreditTotal,
    currentPaymentData.selectedAccountCreditIds,
  )
  trackEvent(TrackingEvent.ADD_PAYMENT_V2_SEND_TO_TERMINAL_CLICKED, {
    timestamp: Date.now(),
    invoiceId: invoice.id,
    invoiceType: getInvoiceType(invoice),
    transactionType: TRANSACTION_TYPE_CHARGE,
    amountToCharge: currentPaymentData.amountToCharge,
    paymentMethod: currentPaymentData.paymentMethod,
    externalDeviceId: currentPaymentData.paymentTerminal || undefined,
    businessId,
    customerId: invoice.clientId,
    accountCreditsApplied,
    invoiceDueToPay: currentPaymentData.originalInvoiceDue || undefined,
  })
}

export interface TrackSendToTerminalInitiatedParams
  extends TrackSendToTerminalClickedParams {
  durationMs?: number
}

export function trackSendToTerminalInitiated({
  invoice,
  currentPaymentData,
  businessId,
  durationMs,
}: TrackSendToTerminalInitiatedParams): void {
  const accountCreditsApplied = accountCreditsSelected(
    currentPaymentData.accountCreditTotal,
    currentPaymentData.selectedAccountCreditIds,
  )
  trackEvent(TrackingEvent.ADD_PAYMENT_V2_SEND_TO_TERMINAL_INITIATED, {
    timestamp: Date.now(),
    invoiceId: invoice.id,
    invoiceType: getInvoiceType(invoice),
    transactionType: TRANSACTION_TYPE_CHARGE,
    amountToCharge: currentPaymentData.amountToCharge,
    paymentMethod: currentPaymentData.paymentMethod,
    externalDeviceId: currentPaymentData.paymentTerminal || undefined,
    businessId,
    customerId: invoice.clientId,
    accountCreditsApplied,
    invoiceDueToPay: currentPaymentData.originalInvoiceDue || undefined,
    ...(durationMs !== undefined && { durationMs }),
  })
}

export interface TrackSendToTerminalInitiatedSuccessParams {
  businessId: string
  currentPaymentData: PaymentData
  durationMs?: number
  externalDeviceId: string
  invoice: InvoiceOrEstimate
  transactionId: string
}

export function trackSendToTerminalInitiatedSuccess({
  invoice,
  businessId,
  currentPaymentData,
  transactionId,
  externalDeviceId,
  durationMs,
}: TrackSendToTerminalInitiatedSuccessParams): void {
  trackEvent(TrackingEvent.ADD_PAYMENT_V2_SEND_TO_TERMINAL_INITIATED_SUCCESS, {
    timestamp: Date.now(),
    invoiceId: invoice.id,
    invoiceType: getInvoiceType(invoice),
    transactionType: TRANSACTION_TYPE_CHARGE,
    paymentMethod: currentPaymentData.paymentMethod,
    amountToCharge: currentPaymentData.amountToCharge,
    businessId,
    customerId: invoice.clientId,
    transactionId,
    externalDeviceId,
    ...(durationMs !== undefined && { durationMs }),
  })
}

export interface TrackTerminalSuccessParams {
  businessId: string
  currentPaymentData: PaymentData
  durationMs?: number
  invoice: InvoiceOrEstimate
  transaction: CompletedSaleTransaction
}

export function trackTerminalSuccess({
  invoice,
  currentPaymentData,
  transaction,
  businessId,
  durationMs,
}: TrackTerminalSuccessParams): void {
  trackEvent(TrackingEvent.ADD_PAYMENT_V2_TERMINAL_SUCCESS, {
    timestamp: Date.now(),
    invoiceId: invoice.id,
    invoiceType: getInvoiceType(invoice),
    transactionType: TRANSACTION_TYPE_CHARGE,
    paymentId: transaction.payment?.id,
    transactionId: transaction?.id,
    amount: transaction.amount,
    amountToCharge: currentPaymentData.amountToCharge,
    authCode: transaction.authCode,
    externalDeviceId: transaction.externalDeviceId,
    externalProcessorId: transaction.externalProcessorId,
    paymentMethod: currentPaymentData.paymentMethod,
    businessId,
    customerId: invoice.clientId,
    originalInvoiceDue: currentPaymentData.originalInvoiceDue,
    ...(durationMs !== undefined && { durationMs }),
  })
}

export interface TrackTerminalFailureParams {
  amount?: number | string
  businessId: string
  durationMs?: number
  errorMessage?: string
  externalDeviceId?: string
  failureReason?: string | null
  failureReasonId?: string
  hardFailure: boolean
  invoice: InvoiceOrEstimate
  paymentMethod: string
  transactionId?: string
  withAccountCredits: boolean
}

export function trackTerminalFailure({
  invoice,
  businessId,
  hardFailure,
  failureReason,
  failureReasonId,
  transactionId,
  amount,
  externalDeviceId,
  paymentMethod,
  withAccountCredits,
  errorMessage,
  durationMs,
}: TrackTerminalFailureParams): void {
  trackEvent(TrackingEvent.ADD_PAYMENT_V2_TERMINAL_FAILURE, {
    timestamp: Date.now(),
    invoiceId: invoice.id,
    invoiceType: getInvoiceType(invoice),
    transactionType: TRANSACTION_TYPE_CHARGE,
    paymentMethod,
    businessId,
    customerId: invoice.clientId,
    hardFailure,
    failureReason: failureReason ?? undefined,
    failureReasonId,
    transactionId,
    amount,
    externalDeviceId,
    withAccountCredits,
    errorMessage,
    ...(durationMs !== undefined && { durationMs }),
  })
}

export interface TrackFlowErrorParams {
  businessId: string
  errorMessage: string
  errorSource: string
  invoice: InvoiceOrEstimate
  paymentMethod?: string
}

export function trackFlowError({
  invoice,
  businessId,
  errorSource,
  errorMessage,
  paymentMethod,
}: TrackFlowErrorParams): void {
  trackEvent(TrackingEvent.ADD_PAYMENT_V2_FLOW_ERROR, {
    timestamp: Date.now(),
    invoiceId: invoice.id,
    invoiceType: getInvoiceType(invoice),
    transactionType: TRANSACTION_TYPE_CHARGE,
    ...(paymentMethod !== undefined && { paymentMethod }),
    businessId,
    customerId: invoice.clientId,
    errorSource,
    errorMessage,
  })
}

export type InvoiceRefreshedTrigger =
  | 'payment_success'
  | 'transaction_failure'
  | 'transaction_hard_failure'

export interface TrackInvoiceRefreshedParams {
  businessId: string
  invoice: InvoiceOrEstimate
  trigger: InvoiceRefreshedTrigger
}

export function trackInvoiceRefreshed({
  invoice,
  businessId,
  trigger,
}: TrackInvoiceRefreshedParams): void {
  trackEvent(TrackingEvent.ADD_PAYMENT_V2_INVOICE_REFRESHED, {
    timestamp: Date.now(),
    invoiceId: invoice.id,
    invoiceType: getInvoiceType(invoice),
    businessId,
    customerId: invoice.clientId,
    trigger,
  })
}
